import { KeyValPair } from "../constant";
import { NestedText, SubSection } from "../_shared/TherapyBlocks";
import { CBT_COGNITIVE_DISTORTIONS } from "../../pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/CbtCognitiveDistortions";
import { CBT_INTERVENTIONS } from "../../pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/CbtInterventionsProvided";
import {
  DBT_SECTIONS,
  DBT_TARGET_BEHAVIOR_OPTIONS,
  DBT_PROGRESS_OPTIONS,
} from "../../pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/DbtComponents";
import {
  ACT_INTERVENTIONS,
  ACT_PROGRESS_OPTIONS,
} from "../../pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/ActComponents";

// Renders the CBT / DBT / ACT modality-specific sections using the REAL nested
// keys the form persists (psychotherapyCbtVisitNote / psychotherapyDbtVisitNote
// / psychotherapyActVisitNote), reusing the form's exported option constants so
// labels stay in sync.

type Item = { readonly key: string; readonly label: string };

const optionLabel = (
  options: readonly { value: string; label: string }[],
  value?: string
) => options.find((o) => o.value === value)?.label;

const sectionHeadStyle = {
  margin: "6px 0 2px",
  fontWeight: "bold" as const,
  fontSize: "14px",
};

// Checkbox + note list: shows each checked flag's label and its `${key}Note`.
// `noteLabel` mirrors the encounter form's label for the free-text field
// (e.g. ACT uses "Client Response", CBT uses "Note") so the PDF matches.
const CheckedNoteList = ({
  obj,
  items,
  noteLabel = "Note",
}: {
  obj: any;
  items: readonly Item[];
  noteLabel?: string;
}) => {
  const checked = items.filter((it) => obj?.[it.key]);
  if (!checked.length) return null;
  return (
    <>
      {checked.map((it) => {
        const note = (obj?.[`${it.key}Note`] as string) || "";
        return (
          <div key={it.key} style={{ marginBottom: 4 }}>
            <p style={{ margin: "2px 0", fontSize: "14px" }}>• {it.label}</p>
            {note.trim() && (
              <p style={{ margin: "2px 0 2px 14px", fontSize: "14px" }}>
                <strong>{noteLabel} : </strong>
                {note}
              </p>
            )}
          </div>
        );
      })}
    </>
  );
};

const CbtBlock = ({ data }: { data: any }) => {
  const cbt = data?.psychotherapyCbtVisitNote;
  const anyIntervention = CBT_INTERVENTIONS.some((it) => cbt?.[it.key]);
  const anyDistortion = CBT_COGNITIVE_DISTORTIONS.some((it) => cbt?.[it.key]);
  if (
    !cbt ||
    (!cbt.cbtSpecificComponents &&
      !anyIntervention &&
      !anyDistortion &&
      !cbt.homeworkSection)
  )
    return null;

  return (
    <SubSection label="CBT-Specific Components">
      <NestedText
        label="CBT Specific Components"
        val={cbt?.cbtSpecificComponents}
      />
      {anyDistortion && (
        <>
          <p style={sectionHeadStyle}>Cognitive Distortions Identified</p>
          <CheckedNoteList obj={cbt} items={CBT_COGNITIVE_DISTORTIONS} />
        </>
      )}
      {anyIntervention && (
        <>
          <p style={sectionHeadStyle}>Interventions Provided</p>
          <CheckedNoteList obj={cbt} items={CBT_INTERVENTIONS} />
        </>
      )}
      <NestedText label="Homework Assigned" val={cbt?.homeworkSection} />
    </SubSection>
  );
};

const DbtBlock = ({ data }: { data: any }) => {
  const dbt = data?.psychotherapyDbtVisitNote;
  if (!dbt) return null;

  const sections = DBT_SECTIONS.map((section) => {
    const checkedSkills = section.items
      .filter((it) => dbt?.[`${section.prefix}${it.suffix}`])
      .map((it) => it.label);
    const response = (dbt?.[section.responseKey] as string) || "";
    return { label: section.label, checkedSkills, response };
  }).filter((s) => s.checkedSkills.length > 0 || s.response.trim());

  const targetBehaviors = optionLabel(
    DBT_TARGET_BEHAVIOR_OPTIONS,
    dbt?.targetBehaviors
  );
  const progress = optionLabel(DBT_PROGRESS_OPTIONS, dbt?.initialProgress);

  if (!sections.length && !targetBehaviors && !progress) return null;

  return (
    <SubSection label="DBT-Specific Components">
      {sections.map((s) => (
        <div key={s.label} style={{ marginBottom: 6 }}>
          <p style={sectionHeadStyle}>{s.label}</p>
          {s.checkedSkills.length > 0 && (
            <p style={{ margin: "2px 0", fontSize: "14px" }}>
              {s.checkedSkills.join(", ")}
            </p>
          )}
          {s.response.trim() && (
            <NestedText label="Client Response" val={s.response} />
          )}
        </div>
      ))}
      {targetBehaviors && (
        <KeyValPair k="Target Behaviors" v={targetBehaviors} />
      )}
      {progress && <KeyValPair k="Progress" v={progress} />}
    </SubSection>
  );
};

const ActBlock = ({ data }: { data: any }) => {
  const act = data?.psychotherapyActVisitNote;
  const anyIntervention = ACT_INTERVENTIONS.some((it) => act?.[it.key]);
  const progress = optionLabel(ACT_PROGRESS_OPTIONS, act?.initialProgress);
  if (
    !act ||
    (!anyIntervention &&
      !progress &&
      !act.initialProgressEvidence &&
      !act.homework)
  )
    return null;

  return (
    <SubSection label="ACT-Specific Components (Hexaflex)">
      {anyIntervention && (
        <>
          <p style={sectionHeadStyle}>Interventions Provided</p>
          <CheckedNoteList
            obj={act}
            items={ACT_INTERVENTIONS}
            noteLabel="Client Response"
          />
        </>
      )}
      {progress && <KeyValPair k="Progress" v={progress} />}
      <NestedText label="Evidence" val={act?.initialProgressEvidence} />
      <NestedText label="Homework Assigned" val={act?.homework} />
    </SubSection>
  );
};

const ModalitySpecificMapper = ({ data }: { data: any }) => {
  const modality = data?.primaryModality;
  if (modality === "Cognitive_Behavioral_Therapy_CBT")
    return <CbtBlock data={data} />;
  if (modality === "Dialectical_Behavior_Therapy_DBT")
    return <DbtBlock data={data} />;
  if (modality === "Acceptance_Commitment_Therapy_ACT")
    return <ActBlock data={data} />;
  return null;
};

export default ModalitySpecificMapper;
